import axios from 'axios';

// Base URL of the backend API - Use the correct server URL
const API_BASE_URL = 'https://api.lab.battlexo.com';

// Default Google Client ID - replace with your actual client ID
const DEFAULT_GOOGLE_CLIENT_ID = '976039673758-uf4mfmgdpgdq0rn93hs9t8uh7okivbjn.apps.googleusercontent.com';

// Helper function to check if we're in offline mode
const isOfflineMode = () => {
  return localStorage.getItem('offlineMode') === 'true';
};

// Create an axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Increase timeout to prevent long waiting periods
  timeout: 30000, // 30 seconds
});

// Add a request interceptor to include the auth token in every request
apiClient.interceptors.request.use(
  (config) => {
    // Log all API requests for debugging
    console.log(`Making ${config.method.toUpperCase()} request to ${config.baseURL}${config.url}`, config.data);
    
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add user ID to every request if available - helps with user-specific endpoints
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const userId = parsedUser.id || parsedUser.user_id;
        if (userId && !config.headers['X-User-ID']) {
          config.headers['X-User-ID'] = userId;
        }
      } catch (e) {
        console.error('Error parsing stored user for request headers:', e);
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor for better error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.data);
    return response;
  },
  (error) => {
    // Network or server errors
    if (error.code === 'ERR_NETWORK' || !error.response) {
      console.error('Network error:', error);
      error.message = 'Network error: Unable to connect to the server. Please check if the backend is running.';
    } 
    // Server responded with an error status code
    else if (error.response) {
      console.error('Server error:', error.response.status, error.response.data);
      
      // Internal Server Error - likely a backend issue
      if (error.response.status === 500) {
        error.message = 'Internal Server Error: The backend server encountered an error. This is likely an issue with the server configuration.';
      }
      // Handle validation errors
      else if (error.response.data && error.response.data.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // FastAPI validation errors come as an array
          const messages = error.response.data.detail.map(err => 
            `${err.loc.join('.')}: ${err.msg}`
          );
          error.message = messages.join('; ');
        } else {
          error.message = error.response.data.detail;
        }
      } 
      // Generic error message
      else {
        error.message = error.response.data.message || 'Server error';
      }
    }
    
    return Promise.reject(error);
  }
);

// Create a MockAPI implementation for testing without a backend server
class MockAPI {
  constructor() {
    // Mock user data
    this.users = [
      { id: "123", email: "test@example.com", firstName: "Test", lastName: "User" }
    ];
    
    // If a token exists in localStorage, consider the user logged in
    this.isLoggedIn = !!localStorage.getItem('authToken');
    
    console.log("⚠️ USING MOCK API - No backend connection required");
  }
  
  // Helper method to simulate API delay
  delay(ms = 800) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  // Mock Google Sign Up
  async googleSignUp(token) {
    await this.delay();
    localStorage.setItem('authToken', 'mock_google_token_123');
    this.isLoggedIn = true;
    return { success: true, token: 'mock_google_token_123' };
  }
  
  // Mock Google Sign In
  async googleSignIn(token) {
    await this.delay();
    if (!this.isLoggedIn) {
      localStorage.setItem('authToken', 'mock_google_token_123');
      this.isLoggedIn = true;
    }
    return { token: 'mock_google_token_123', user: this.users[0] };
  }
  
  // Mock Login
  async login(credentials) {
    await this.delay();
    if (credentials.email && credentials.password) {
      localStorage.setItem('authToken', 'mock_token_123');
      this.isLoggedIn = true;
      return { token: 'mock_token_123', user: this.users[0] };
    } else {
      throw new Error('Invalid credentials');
    }
  }
  
  // Mock Register
  async register(userData) {
    await this.delay();
    if (!userData.email || !userData.password) {
      throw new Error('Email and password are required');
    }
    if (userData.password !== userData.confirmPassword) {
      throw new Error('Passwords do not match');
    }
    
    localStorage.setItem('authToken', 'mock_token_456');
    this.isLoggedIn = true;
    return { 
      success: true, 
      message: 'User registered successfully',
      token: 'mock_token_456',
      user: {
        id: "456",
        email: userData.email,
        firstName: userData.first_name || '',
        lastName: userData.last_name || ''
      }
    };
  }
}

// Create axios instance for auth API
const authAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000
});

// Add better error handling
authAPI.interceptors.response.use(
  (response) => {
    console.log('Auth API Response:', response.status, response.data);
    return response;
  },
  (error) => {
    if (error.response) {
      // Server responded with an error status
      console.error('Auth API Error:', error.response.status, error.response.data);
      
      if (error.response.status === 401) {
        console.log('Authentication failed: Invalid credentials or user does not exist');
        
        // Try to give a helpful error message
        if (error.response.data && error.response.data.detail) {
          error.message = error.response.data.detail;
        } else {
          error.message = 'Invalid email or password';
        }
      } else if (error.response.status === 500) {
        console.error('Server error:', error.response.data);
        error.message = 'Server error: The backend encountered an issue';
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('Auth API No Response:', error.request);
      error.message = 'No response from server. Please check if the backend is running.';
    } else {
      // Something else happened
      console.error('Auth API Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Authentication Services
export const createUser = async (userData) => {
  try {
    // Use the data directly since field names should already be formatted correctly
    console.log('Sending registration data:', userData);
    
    const response = await authAPI.post('/api/v1/auth/register', userData);
    console.log('Registration response:', response.data);
    
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
    }
    
    if (response.data.user) {
      localStorage.setItem('currentUser', JSON.stringify(response.data.user));
    }
    
    return response.data;
  } catch (error) {
    console.error('Registration error:', error.response?.data || error.message);
    
    // Extract validation errors if they exist
    if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
      const validationErrors = error.response.data.detail.map(err => 
        `${err.loc.slice(-1)[0]}: ${err.msg}`
      ).join('; ');
      
      throw new Error(validationErrors || 'Registration failed');
    }
    
    throw error.response?.data?.message || error.message || 'Registration failed';
  }
};

export const signInWithEmail = async (email, password) => {
  try {
    console.log('Attempting to login with email:', email);
    const response = await authAPI.post('/api/v1/auth/login', { email, password });
    console.log('Login response:', response.data);
    
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
    }
    
    if (response.data.user) {
      localStorage.setItem('currentUser', JSON.stringify(response.data.user));
    }
    
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    
    // Handle specific error cases
    if (error.code === 'ERR_NETWORK') {
      throw new Error('Network error: Could not connect to the authentication server. Please check your internet connection or try again later.');
    }
    
    if (error.response) {
      // Handle common HTTP status codes
      if (error.response.status === 401) {
        throw new Error('Invalid email or password. Please try again.');
      } else if (error.response.status === 404) {
        throw new Error('User not found. Please check your email or register for a new account.');
      } else if (error.response.status === 429) {
        throw new Error('Too many login attempts. Please try again later.');
      } else if (error.response.status >= 500) {
        throw new Error('Server error. The authentication service is currently unavailable. Please try again later.');
      }
      
      // Extract error message from response if available
      const errorMessage = error.response.data?.detail || error.response.data?.message || 'Login failed. Please try again.';
      throw new Error(errorMessage);
    }
    
    // For all other errors
    throw new Error('Login failed. Please check your credentials and try again.');
  }
};

// Add this new function for Google Sign-Up
export const signUpWithGoogle = async () => {
  try {
    console.log("🔐 Google Sign-Up: Starting authentication");
    
    // We need to load Firebase SDK dynamically
    if (typeof window.firebase === 'undefined') {
      console.log("Loading Firebase SDK...");
      
      // Load Firebase App
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://www.gstatic.com/firebasejs/8.10.0/firebase-app.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      
      // Load Firebase Auth
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://www.gstatic.com/firebasejs/8.10.0/firebase-auth.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      
      console.log("Firebase SDK loaded successfully");
    }
    
    // Initialize Firebase if not already initialized
    if (!window.firebase.apps || !window.firebase.apps.length) {
      const firebaseConfig = {
        apiKey: "AIzaSyB-Ga9bUSax9HjI_DNtXMvjguS6hgK1YAg",
        authDomain: "game-ai-504f3.firebaseapp.com",
        projectId: "game-ai-504f3",
        storageBucket: "game-ai-504f3.appspot.com",
        messagingSenderId: "598632257513",
        appId: "1:598632257513:web:0f55b5e9eb42f998b8aa53"
      };
      
      window.firebase.initializeApp(firebaseConfig);
      console.log("Firebase initialized successfully");
    }
    
    // Use Firebase for Google authentication
    const auth = window.firebase.auth();
    const provider = new window.firebase.auth.GoogleAuthProvider();
    
    // Add scopes
    provider.addScope('profile');
    provider.addScope('email');
    
    // Set custom parameters
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    // Sign in with popup
    console.log("Opening Google sign-in popup...");
    const result = await auth.signInWithPopup(provider);
    
    if (!result.user) {
      throw new Error("No user returned from Google sign-in");
    }
    
    // Get token
    const token = await result.user.getIdToken(true); // Force token refresh
    console.log("Got ID token from Firebase:", token.substring(0, 10) + "...");
    
    // Prepare the request data
    const requestData = {
      token: token
    };
    
    console.log("Sending data to backend at https://api.lab.battlexo.com/api/v1/auth/google/signup");
    console.log("Request data:", requestData);
    
    const response = await axios.post("https://api.lab.battlexo.com/api/v1/auth/google/signup", requestData, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    console.log("✅ Google Sign-Up Success:", response.data);
    
    return {
      ...response.data,
      message: "Successfully signed up"
    };
  } catch (error) {
    console.error("❌ Google Sign-Up Failed:", error);
    
    // Show the actual error 
    const errorMessage = error.response?.data?.message || error.message || "Authentication failed";
    console.error("Authentication error details:", errorMessage);
    
    // Rethrow the error to be handled by the UI
    throw error;
  }
};

// Keep the original signInWithGoogle function with the correct endpoint
export const signInWithGoogle = async () => {
  try {
    console.log("🔐 Google Sign-In: Starting authentication");
    
    // We need to load Firebase SDK dynamically
    if (typeof window.firebase === 'undefined') {
      console.log("Loading Firebase SDK...");
      
      // Load Firebase App
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://www.gstatic.com/firebasejs/8.10.0/firebase-app.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      
      // Load Firebase Auth
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://www.gstatic.com/firebasejs/8.10.0/firebase-auth.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      
      console.log("Firebase SDK loaded successfully");
    }
    
    // Initialize Firebase if not already initialized
    if (!window.firebase.apps || !window.firebase.apps.length) {
      const firebaseConfig = {
        apiKey: "AIzaSyB-Ga9bUSax9HjI_DNtXMvjguS6hgK1YAg",
        authDomain: "game-ai-504f3.firebaseapp.com",
        projectId: "game-ai-504f3",
        storageBucket: "game-ai-504f3.appspot.com",
        messagingSenderId: "598632257513",
        appId: "1:598632257513:web:0f55b5e9eb42f998b8aa53"
      };
      
      window.firebase.initializeApp(firebaseConfig);
      console.log("Firebase initialized successfully");
    }
    
    // Use Firebase for Google authentication
    const auth = window.firebase.auth();
    const provider = new window.firebase.auth.GoogleAuthProvider();
    
    // Add scopes
    provider.addScope('profile');
    provider.addScope('email');
    
    // Set custom parameters
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    // Sign in with popup
    console.log("Opening Google sign-in popup...");
    const result = await auth.signInWithPopup(provider);
    
    if (!result.user) {
      throw new Error("No user returned from Google sign-in");
    }
    
    // Get token
    const token = await result.user.getIdToken(true); // Force token refresh
    console.log("Got ID token from Firebase:", token.substring(0, 10) + "...");
    
    // Prepare the request data
    const requestData = {
      token: token
    };
    
    console.log("Sending data to backend at https://api.lab.battlexo.com/api/v1/auth/google/signin");
    console.log("Request data:", requestData);
    
    const response = await axios.post("https://api.lab.battlexo.com/api/v1/auth/google/signin", requestData, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    console.log("✅ Google Sign-In Success:", response.data);
    
    return response.data;
  } catch (error) {
    console.error("❌ Google Sign-In Failed:", error);
    
    // Show the actual error 
    const errorMessage = error.response?.data?.message || error.message || "Authentication failed";
    console.error("Authentication error details:", errorMessage);
    
    // Rethrow the error to be handled by the UI
    throw error;
  }
};

export const resetPassword = async (email) => {
  try {
    const response = await authAPI.post('/api/v1/auth/reset-password', { email });
    return response.data;
  } catch (error) {
    console.error('Password reset error:', error.response?.data || error.message);
    throw error.response?.data?.message || error.message || 'Password reset failed';
  }
};

export const signOut = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('currentUser');
};

// Chat APIs
export const chatAPI = {
  // Get all chat sessions for a user
  getSessions: async (userId) => {
    try {
      // First try from the server API
      try {
        const response = await apiClient.get(`/api/v1/groq/sessions/${userId}`);
        return response.data;
      } catch (serverError) {
        console.warn('Server API failed, falling back to localStorage:', serverError);
        
        // Fallback to localStorage for offline/demo mode
        const sessions = [];
        // Scan localStorage for sessions belonging to this user
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key.startsWith(`chat_${userId}_`)) {
            try {
              const sessionData = JSON.parse(localStorage.getItem(key));
              if (sessionData && sessionData.id) {
                sessions.push({
                  id: sessionData.id,
                  user_id: userId,
                  title: sessionData.title || 'Untitled Chat',
                  created_at: sessionData.createdAt || new Date().toISOString(),
                  updated_at: sessionData.updatedAt || new Date().toISOString(),
                  message_count: sessionData.messages?.length || 0
                });
              }
            } catch (e) {
              console.error('Error parsing session data:', e);
            }
          }
        }
        return sessions;
      }
    } catch (error) {
      console.error('Get sessions error:', error.message);
      throw error;
    }
  },

  // Get messages for a specific session
  getSessionMessages: async (sessionId, userId) => {
    try {
      // Try server API with newer format first
      try {
        console.log(`Fetching messages for session ${sessionId} from API endpoint`);
        
        // Try the primary endpoint format
        const response = await apiClient.get(`/api/v1/groq/session/${sessionId}/messages`);
        
        // Check if response has a messages array property
        if (response.data && Array.isArray(response.data)) {
          console.log(`Got ${response.data.length} messages directly from API`);
          return response.data;
        } else if (response.data && Array.isArray(response.data.messages)) {
          console.log(`Got ${response.data.messages.length} messages from API response.data.messages`);
          return response.data.messages;
        } else {
          console.log('API response format not recognized:', response.data);
          throw new Error('Unrecognized API response format');
        }
      } catch (primaryEndpointError) {
        console.warn('Primary endpoint failed, trying alternate endpoint:', primaryEndpointError);
        
        // Try the alternate endpoint format (with api)
        try {
          const altResponse = await apiClient.get(`/api/v1/groq/sessions/${sessionId}/messages`);
          if (altResponse.data && Array.isArray(altResponse.data)) {
            console.log(`Got ${altResponse.data.length} messages from alternate API endpoint`);
            return altResponse.data;
          } else if (altResponse.data && Array.isArray(altResponse.data.messages)) {
            console.log(`Got ${altResponse.data.messages.length} messages from alternate API endpoint's data.messages`);
            return altResponse.data.messages;
          }
        } catch (alternateEndpointError) {
          console.warn('Alternate endpoint failed:', alternateEndpointError);
          
          // Try without the 'api' prefix
          try {
            const noApiResponse = await axios.get(`https://api.lab.battlexo.com/api/v1/groq/session/${sessionId}/messages`);
            if (noApiResponse.data && Array.isArray(noApiResponse.data)) {
              console.log(`Got ${noApiResponse.data.length} messages from direct endpoint`);
              return noApiResponse.data;
            } else if (noApiResponse.data && Array.isArray(noApiResponse.data.messages)) {
              console.log(`Got ${noApiResponse.data.messages.length} messages from direct endpoint's data.messages`);
              return noApiResponse.data.messages;
            }
          } catch (directEndpointError) {
            console.warn('Direct endpoint failed, falling back to localStorage:', directEndpointError);
          }
        }
        
        // Fallback to localStorage for offline/demo mode
        const key = `chat_${userId}_${sessionId}`;
        const sessionData = localStorage.getItem(key);
        if (sessionData) {
          try {
            const parsed = JSON.parse(sessionData);
            return parsed.messages || [];
          } catch (e) {
            console.error('Error parsing session messages:', e);
          }
        }
        return [];
      }
    } catch (error) {
      console.error('Get session messages error:', error.message);
      throw error;
    }
  },

  // Create a new session
  createSession: async (userId, title = "New Chat") => {
    try {
      // Try server API first
      try {
        const response = await apiClient.post('/api/v1/groq/session', {
          user_id: userId,
          title
        });
        return response.data;
      } catch (serverError) {
        console.warn('Server API failed, creating local session:', serverError);
        
        // Fallback to localStorage for offline/demo mode
        const sessionId = `local_${Date.now()}`;
        const sessionData = {
          id: sessionId,
          userId,
          title,
          messages: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        localStorage.setItem(`chat_${userId}_${sessionId}`, JSON.stringify(sessionData));
        return { 
          id: sessionId,
          user_id: userId,
          title,
          created_at: sessionData.createdAt,
          updated_at: sessionData.updatedAt
        };
      }
    } catch (error) {
      console.error('Create session error:', error.message);
      throw error;
    }
  },

  // Update session title
  updateSessionTitle: async (sessionId, title, userId) => {
    try {
      // First check if we're in offline mode
      if (isOfflineMode()) {
        console.log(`Offline mode: Updating session ${sessionId} title to "${title}"`);
        
        // Get stored sessions from localStorage
        const storedSessions = JSON.parse(localStorage.getItem('userSessions') || '[]');
        
        // Find and update the session
        const updatedSessions = storedSessions.map(session => {
          if (session.id === sessionId) {
            return { ...session, title };
          }
          return session;
        });
        
        // Save back to localStorage
        localStorage.setItem('userSessions', JSON.stringify(updatedSessions));
        
        return { success: true, message: 'Session title updated in offline mode' };
      }
      
      // Make the API call to update the session title
      const response = await axios.put(`https://api.lab.battlexo.com/api/v1/groq/session/${sessionId}/title?title=${encodeURIComponent(title)}`, {
        userId
      });
      
      return response.data;
    } catch (error) {
      console.error('Error updating session title:', error);
      
      // If we get a network error, try to update in localStorage as a fallback
      if (error.message.includes('Network Error')) {
        console.log('Network error, updating session title in localStorage as fallback');
        
        // Get stored sessions
        const storedSessions = JSON.parse(localStorage.getItem('userSessions') || '[]');
        
        // Update the session
        const updatedSessions = storedSessions.map(session => {
          if (session.id === sessionId) {
            return { ...session, title };
          }
          return session;
        });
        
        // Save back to localStorage
        localStorage.setItem('userSessions', JSON.stringify(updatedSessions));
        
        return { success: true, message: 'Session title updated in localStorage due to API error' };
      }
      
      throw error;
    }
  },

  // Delete a session
  deleteSession: async (sessionId, userId) => {
    try {
      // Try server API first
      try {
        const response = await apiClient.delete(`/api/v1/groq/session/${sessionId}`);
        
        // Also remove from localStorage if it exists there
        const key = `chat_${userId}_${sessionId}`;
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
        }
        
        return response.data;
      } catch (serverError) {
        console.warn('Server API failed, deleting local session:', serverError);
        
        // Fallback to localStorage for offline/demo mode
        const key = `chat_${userId}_${sessionId}`;
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          return { success: true, message: 'Session deleted from localStorage' };
        }
        throw new Error('Session not found in localStorage');
      }
    } catch (error) {
      console.error('Delete session error:', error.message);
      throw error;
    }
  },

  // Send a message (non-streaming)
  sendMessage: async (sessionId, userId, message, title = "") => {
    try {
      console.log('Sending message to API:', { sessionId, userId, title, messageLength: message.length });
      
      // Try server API first
      try {
        const response = await apiClient.post('/api/v1/groq/chat', {
          session_id: sessionId,
          user_id: userId,
          title,
          message,
        });
        
        // If we got a response but had no sessionId initially, we should save the new
        // sessionId to local storage for this user
        if (!sessionId && response.data && response.data.session_id) {
          const newSessionId = response.data.session_id;
          // Create a localStorage entry for this session
          const newSessionData = {
            id: newSessionId,
            userId,
            title: title || 'My Project',
            messages: [
              { id: Date.now(), sender: 'user', text: message },
              { id: Date.now() + 1, sender: 'ai', text: response.data.response }
            ],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          localStorage.setItem(`chat_${userId}_${newSessionId}`, JSON.stringify(newSessionData));
        }
        
        return response.data;
      } catch (serverError) {
        console.error('Server API error:', serverError);
        
        // Check if we have a connection error
        if (serverError.code === 'ERR_NETWORK' || !serverError.response) {
          console.warn('Network error, using mock response');
        } else {
          // We have a server response, but it's an error
          console.warn(`Server responded with error ${serverError.response?.status}: ${serverError.response?.statusText}`);
        }
        
        // Handle the error more gracefully with a real error message
        const errorResponse = {
          response: "I'm sorry, I couldn't process your request. The server may be experiencing issues or undergoing maintenance. Please try again later.",
          session_id: sessionId || `local_${Date.now()}`
        };
        
        // Save to localStorage for persistence
        const activeSessionId = sessionId || errorResponse.session_id;
        const key = `chat_${userId}_${activeSessionId}`;
        
        try {
          let sessionData = null;
          const existing = localStorage.getItem(key);
          
          if (existing) {
            sessionData = JSON.parse(existing);
            sessionData.messages.push(
              { id: Date.now(), sender: 'user', text: message },
              { id: Date.now() + 1, sender: 'ai', text: errorResponse.response }
            );
            sessionData.updatedAt = new Date().toISOString();
          } else {
            sessionData = {
              id: activeSessionId,
              userId,
              title: title || 'My Project',
              messages: [
                { id: Date.now(), sender: 'user', text: message },
                { id: Date.now() + 1, sender: 'ai', text: errorResponse.response }
              ],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
          }
          localStorage.setItem(key, JSON.stringify(sessionData));
        } catch (e) {
          console.error('Error saving error response to localStorage:', e);
        }
        
        return errorResponse;
      }
    } catch (error) {
      console.error('Send message error:', error.message);
      throw error;
    }
  },

  // Stream a message response (for real-time chat)
  streamMessage: async (sessionId, userId, message, title = "", onChunk) => {
    try {
      console.log('Starting stream request with session:', sessionId, 'user:', userId);
      
      // Configure axios for streaming text responses with fine-grained chunks
      const response = await apiClient.post('/api/v1/groq/chat/stream', {
        session_id: sessionId,
        user_id: userId,
        title,
        message,
      }, {
        responseType: 'text',
        
        // Handle streaming response data - enable as real-time as possible
        onDownloadProgress: (progressEvent) => {
          // Safety check that we have a valid callback
          if (typeof onChunk !== 'function') {
            console.warn('No valid chunk handler provided for streaming');
            return;
          }
          
          // Check if we have valid responseText
          if (!progressEvent?.currentTarget?.responseText) {
            console.warn('No responseText in progress event');
            return;
          }
          
          // Pass the entire progress event to the handler
          try {
            // Log exact chunk sizes for debugging
            console.log(`API chunk received: ${progressEvent.currentTarget.responseText.length} total chars`);
            onChunk(progressEvent);
          } catch (e) {
            console.error('Error in chunk handler:', e);
          }
        },
        
        // Set xhr configs for more real-time streaming
        xhrAdapter: true,
        
        // These settings help get smaller chunks and more frequent updates
        beforeSend: (xhr) => {
          // Disable buffering for more immediate chunks
          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          xhr.setRequestHeader('Cache-Control', 'no-cache');
        },
        
        // Increase timeout for longer responses
        timeout: 60000, // 60 seconds
      });
      
      // If this was a new session (no sessionId provided), extract the session ID
      // from the response and save it for future use
      if (!sessionId && response.headers && response.headers['x-session-id']) {
        const newSessionId = response.headers['x-session-id'];
        console.log('Received new session ID from streaming response:', newSessionId);
      }
      
      console.log('Stream completed successfully with status:', response.status);
      
      // Save the completed conversation to localStorage for this user and session
      if (sessionId) {
        try {
          const key = `chat_${userId}_${sessionId}`;
          let sessionData = null;
          const existing = localStorage.getItem(key);
          
          if (existing) {
            sessionData = JSON.parse(existing);
            // Add the user message
            sessionData.messages.push({ 
              id: Date.now(), 
              sender: 'user', 
              text: message 
            });
            
            // Add the AI response
            sessionData.messages.push({ 
              id: Date.now() + 1, 
              sender: 'ai', 
              text: response.data 
            });
            
            sessionData.updatedAt = new Date().toISOString();
          } else {
            // Create new session data
            sessionData = {
              id: sessionId,
              userId,
              title: title || 'My Project',
              messages: [
                { id: Date.now(), sender: 'user', text: message },
                { id: Date.now() + 1, sender: 'ai', text: response.data }
              ],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
          }
          
          localStorage.setItem(key, JSON.stringify(sessionData));
        } catch (e) {
          console.error('Error saving chat to localStorage:', e);
        }
      }
      
      return { 
        success: true,
        status: response.status,
        statusText: response.statusText,
        fullText: response.data // Include the complete response text
      };
    } catch (error) {
      console.error('Stream message error:', error.message || 'Unknown error');
      // Try to extract response text even from error response
      let partialText = '';
      try {
        if (error.response && error.response.data) {
          partialText = error.response.data;
        }
      } catch (e) {
        console.error('Error extracting partial response:', e);
      }
      
      // Throw with additional context
      const enhancedError = new Error(error.message || 'Stream message failed');
      enhancedError.partialText = partialText;
      throw enhancedError;
    }
  },
};

// Fetch user sessions
export const fetchUserSessions = async (userId) => {
  try {
    const response = await fetch(`https://api.lab.battlexo.com/api/v1/groq/sessions/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user sessions:', error);
    throw error;
  }
};

export default apiClient; 